import { Button, Flex, Stack } from '@chakra-ui/react';
import { getTenantByHost, getTenantId } from '@pelicargo/tenants';
import { Show, useError, useSuccess } from '@pelicargo/ui';
import { getPermissionOptions, permissionHierarchy, PermissionLevel } from '@pelicargo/utils';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm, useFormContext, useWatch } from 'react-hook-form';

import { trpc, TrpcOutputs } from '../../config/trpc';
import { useAuth } from '../../context/AuthProvider';
import { UserInput } from '../Autocomplete/UserInput';
import { SimpleFieldArray, SimpleFieldArrayForm } from '../SimpleFieldArray';
import { SimpleForm } from '../SimpleForm';
import { SimpleSelect } from '../SimpleSelect';
import { TypesenseSelect } from '../TypesenseSelect';

type Props = {} & (
  | {
      accountId: number;
      userId?: never;
    }
  | {
      accountId?: never;
      userId: number;
    }
);

const PermissionField = ({
  prefix,
  userId,
  accountId,
}: SimpleFieldArrayForm & {
  // Just used for determining what field to show
  accountId?: number;
  userId?: number;
}) => {
  const { permission, knownTenants } = useAuth();
  const methods = useFormContext();
  const info: TrpcOutputs['getPermissions']['permissions'][number] = useWatch({
    control: methods.control,
    name: prefix,
  });

  const isDisabled = permissionHierarchy[permission] < permissionHierarchy[info.role];

  return (
    <Flex gap={2}>
      <SimpleSelect name={`${prefix}.role`} isDisabled={isDisabled} options={getPermissionOptions(permission)} w={64} />
      <Flex flexDir="column" w="64">
        <Show if={!!userId}>
          <TypesenseSelect
            onChange={({ value }) => {
              methods.setValue(`${prefix}.account_id`, value);
            }}
            name={`${prefix}.account_id`}
            collection="accounts"
            queryBy="name,account_id"
            documentLabel="name"
            documentValue="account_id"
            shouldProcessValue
            suffixWithValue
            isMulti={false}
            isDisabled={isDisabled}
          />
        </Show>
        <Show if={!!accountId}>
          <UserInput name={`${prefix}.user_id`} isDisabled={isDisabled} />
        </Show>
      </Flex>
      <Show if={permission === PermissionLevel.SUPERADMIN || getTenantByHost().isSystemTenant}>
        <SimpleSelect
          w="32"
          name={`${prefix}.tenant_id`}
          options={knownTenants.map((t) => ({
            label: t,
            value: t,
          }))}
        />
      </Show>
    </Flex>
  );
};

export const PermissionForm = ({ accountId, userId }: Props) => {
  const { data: permissions, refetch } = trpc.getPermissions.useQuery({
    accountId,
    userId,
  });

  const { handleError } = useError();
  const { handleSuccess } = useSuccess();

  const { reset, getValues, setValue, ...methods } = useForm<{
    permissions: TrpcOutputs['getPermissions']['permissions'];
    deleted: number[];
  }>({
    defaultValues: {
      permissions: permissions?.['permissions'],
      deleted: [],
    },
  });

  useEffect(() => {
    reset({
      deleted: [],
      permissions: permissions?.['permissions'],
    });
  }, [permissions, reset]);

  const onRemove = useCallback(
    (index: number) => {
      const deleted = getValues()['permissions'][index];
      setValue('deleted', [...getValues()['deleted'], deleted.id]);
    },
    [getValues, setValue],
  );

  const { mutateAsync: save } = trpc.modifyPermissions.useMutation();

  const handleSubmit = methods.handleSubmit(async ({ deleted, permissions }) => {
    try {
      await save({
        deleted,
        permissions,
      });
      await refetch();
      handleSuccess('Updated permissions successfully');
    } catch (e) {
      handleError(e.message);
    }
  });

  const defaultValue = useMemo(() => {
    if (accountId) {
      return {
        account_id: accountId,
        tenant_id: getTenantId(),
      };
    } else if (userId) {
      return {
        user_id: userId,
        tenant_id: getTenantId(),
      };
    }
    return {
      tenant_id: getTenantId(),
    };
  }, [userId, accountId]);

  return (
    <SimpleForm reset={reset} getValues={getValues} setValue={setValue} {...methods}>
      <Stack>
        <SimpleFieldArray
          name={'permissions'}
          canAdd={true}
          canRemove={true}
          onRemove={onRemove}
          form={(args) => <PermissionField accountId={accountId} userId={userId} {...args} />}
          defaultValue={defaultValue}
        />
        <Button onClick={handleSubmit} colorScheme="primary">
          Save
        </Button>
      </Stack>
    </SimpleForm>
  );
};
